import { z } from 'zod';

/* Slug + node id rules match the rest of the codebase. */
const slugRx = /^[฀-๿a-z0-9]+(?:-[฀-๿a-z0-9]+)*$/u;

const pageSlugSchema = z
  .string()
  .trim()
  .min(1)
  .max(80)
  .regex(slugRx, 'slug ใช้ได้: ไทย / a-z / 0-9 และ - เท่านั้น');

const slotIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(60)
  .regex(/^[a-z0-9][a-z0-9-]*$/i, 'slot id ใช้ได้แค่ a-z, 0-9, -');

const articleSourceSchema = z.object({
  kind: z.literal('article'),
  postId: z.string().trim().min(1, 'กรุณาเลือก post'),
});

const inlineSourceSchema = z.object({
  kind: z.literal('inline'),
  title: z.string().trim().min(1, 'กรุณาใส่หัวข้อ').max(200),
  thumbUrl: z
    .string()
    .trim()
    .max(2000)
    .url('thumb url ไม่ถูกต้อง')
    .nullable()
    .or(z.literal('').transform(() => null))
    .default(null),
  linkUrl: z.string().trim().min(1, 'กรุณาใส่ลิงก์').max(2000).url('ลิงก์ไม่ถูกต้อง'),
});

export const slotOverrideSourceSchema = z.discriminatedUnion('kind', [
  articleSourceSchema,
  inlineSourceSchema,
]);

/** Window validity — `endsAt > startsAt`. Both ISO strings. */
const windowSchema = z
  .object({
    startsAt: z.string().datetime({ message: 'startsAt ต้องเป็น ISO datetime' }),
    endsAt: z.string().datetime({ message: 'endsAt ต้องเป็น ISO datetime' }),
  })
  .refine((v) => new Date(v.endsAt).getTime() > new Date(v.startsAt).getTime(), {
    message: 'endsAt ต้องอยู่หลัง startsAt',
    path: ['endsAt'],
  });

/** 0-based slide index when targeting a Carousel; max 19 mirrors the
 *  group_contents `maxCount` cap. Optional (null/omit) for Slot targets;
 *  semantic check (slot vs carousel) happens at the route handler against
 *  the page's current layout. */
const slideIndexSchema = z.number().int().min(0).max(19).nullable().optional();

export const createSlotOverrideSchema = z
  .object({
    pageSlug: pageSlugSchema,
    slotId: slotIdSchema,
    slideIndex: slideIndexSchema,
    source: slotOverrideSourceSchema,
  })
  .and(windowSchema);

export type CreateSlotOverrideInput = z.infer<typeof createSlotOverrideSchema>;

export const updateSlotOverrideSchema = z
  .object({
    source: slotOverrideSourceSchema.optional(),
    startsAt: z.string().datetime().optional(),
    endsAt: z.string().datetime().optional(),
  })
  .refine(
    (v) => {
      // Only validate when BOTH are present in the patch (admin may patch
      // only one — the merged-with-existing window is re-checked at the
      // route handler).
      if (v.startsAt && v.endsAt) {
        return new Date(v.endsAt).getTime() > new Date(v.startsAt).getTime();
      }
      return true;
    },
    { message: 'endsAt ต้องอยู่หลัง startsAt', path: ['endsAt'] },
  );

export type UpdateSlotOverrideInput = z.infer<typeof updateSlotOverrideSchema>;

export const listSlotOverridesQuerySchema = z.object({
  pageSlug: pageSlugSchema.optional(),
  /** 'all' (default) | 'active' | 'upcoming' | 'expired'. */
  status: z.enum(['all', 'active', 'upcoming', 'expired']).default('all'),
});

export type ListSlotOverridesQuery = z.infer<typeof listSlotOverridesQuerySchema>;
